import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'

// AB#2216 — a reusable modal shell, generalised from the overlay DialogHost has used since AB#1846
// (Dialogs.tsx:61). DialogHost stays a promise-based singleton for confirm/prompt/alert; this is the
// version that takes arbitrary children, for cases like the line-items row form that need a whole
// form rather than a message. DialogHost is deliberately NOT refactored onto this — that is app-wide
// behaviour and belongs in its own change.
//
// Deliberately NOT portalled. `@marque/ui` carries no @types/react-dom (which is why
// v3/react-dom-server.d.ts exists), and a portalled subtree renders as nothing under
// renderToStaticMarkup — the mechanism this package's entire test suite is built on. A `fixed`
// overlay is sufficient: no ancestor in the tenant's view tree establishes a transform containing
// block, so `fixed` resolves against the viewport as intended.
//
// z-index band: 990, deliberately BELOW DialogHost's z-[1000] (Dialogs.tsx:63) so a confirm raised
// from inside this modal stacks above it, and below ToastHost's z-[1100] so toasts stay visible.

export interface ModalProps {
    title: string
    onCancel: () => void
    // AB#2521 — optional: a modal showing READ-ONLY content (the pricing analysis) has nothing to
    // confirm. Omitting it renders the dismiss button alone, defaulting its label to "Close" — two
    // buttons that both just dismiss would ask the user to choose between identical outcomes.
    onConfirm?: () => void
    confirmLabel?: string
    cancelLabel?: string
    confirmDisabled?: boolean
    children: ReactNode
    /** Full-height workspace with an independently scrolling body (for chat, editors, etc.). */
    workspace?: boolean
}

export function Modal({ title, onCancel, onConfirm, confirmLabel, cancelLabel, confirmDisabled, children, workspace = false }: ModalProps) {
    const titleId = useId()
    const cardRef = useRef<HTMLDivElement>(null)

    // Restore focus to whatever opened the modal, and move focus inside on mount so the keyboard
    // user is not left behind the backdrop.
    useEffect(() => {
        const opener = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null
        const first = cardRef.current?.querySelector<HTMLElement>('[data-autofocus]') ?? cardRef.current?.querySelector<HTMLElement>(
            'input, select, textarea, button, [href], [tabindex]:not([tabindex="-1"])')
        first?.focus()
        return () => opener?.focus?.()
    }, [])

    // Trap Tab inside the card. Escape cancels. Both are handled on the card rather than the
    // document so a modal nested under another dialog does not steal its keys.
    const onKeyDown = (e: React.KeyboardEvent) => {
        if ((e.target as HTMLElement).closest('[role="dialog"]') !== cardRef.current) return
        if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return }
        if (e.key !== 'Tab') { return }
        const focusable = Array.from(cardRef.current?.querySelectorAll<HTMLElement>(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? [])
            .filter(element => element.getClientRects().length > 0 && element.closest('[role="dialog"]') === cardRef.current)
        if (focusable.length === 0) { return }
        const firstEl = focusable[0]!
        const lastEl = focusable[focusable.length - 1]!
        const active = document.activeElement
        if (e.shiftKey && active === firstEl) { e.preventDefault(); lastEl.focus() }
        else if (!e.shiftKey && active === lastEl) { e.preventDefault(); firstEl.focus() }
    }

    return (
        <div
            className={`fixed inset-0 z-[990] flex items-center justify-center bg-black/40 ${workspace ? 'sm:p-4' : 'p-4'}`}
            // mousedown (not click) and an explicit target check, mirroring Dialogs.tsx:64 — a drag
            // that starts inside the card and ends on the backdrop must not discard a half-typed row.
            onMouseDown={e => { if (e.target === e.currentTarget) { onCancel() } }}
        >
            <div ref={cardRef} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}
                className={`marque-panel w-full bg-white dark:bg-slate-800 shadow-xl border border-neutral-200 dark:border-slate-700 ${workspace ? 'marque-workspace-modal max-w-7xl h-[100dvh] sm:h-[92dvh] flex flex-col overflow-hidden sm:rounded-xl' : 'max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg p-5'}`}>
                <div className={workspace ? 'flex shrink-0 items-center justify-between border-b border-neutral-200 dark:border-slate-700 px-5 py-3' : ''}>
                    <h2 id={titleId} className={`text-sm font-semibold text-neutral-900 dark:text-slate-100 ${workspace ? '' : 'mb-3'}`}>{title}</h2>
                    {workspace && <button type="button" onClick={onCancel} className="marque-button rounded-md px-3 min-h-11 text-sm hover:bg-neutral-100 dark:hover:bg-slate-700">Close</button>}
                </div>
                {children}
                {!workspace && <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onCancel}
                        className="marque-button px-3 py-1.5 text-sm rounded-md border border-neutral-200 dark:border-slate-600 text-neutral-700 dark:text-slate-300 hover:bg-neutral-50 dark:hover:bg-slate-700">
                        {cancelLabel ?? (onConfirm ? 'Cancel' : 'Close')}
                    </button>
                    {onConfirm && (
                        <button type="button" onClick={onConfirm} disabled={confirmDisabled}
                            className="marque-button px-3 py-1.5 text-sm rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                            {confirmLabel ?? 'Save'}
                        </button>
                    )}
                </div>}
            </div>
        </div>
    )
}
